import axios, { CancelToken } from 'axios';

import {
  RetentionMode,
  SubscriberCohortsReport,
} from '../models/SubscriberCohortsModel';

const API_URL = process.env.REACT_APP_API_URL;

// The backend runs two statements with a 30s timeout each; past this the
// connection is considered hung.
const REPORT_REQUEST_TIMEOUT_MS = 60000;
// The XLSX also builds one row per plan (~45k over the whole history).
const DOWNLOAD_REQUEST_TIMEOUT_MS = 120000;

export interface SubscriberCohortsParams {
  since: string; // YYYY-MM
  until: string; // YYYY-MM
  storeId: number | null;
}

function toQueryParams({ since, until, storeId }: SubscriberCohortsParams) {
  return storeId === null
    ? { since, until }
    : { since, until, store_id: storeId };
}

// Recent responses by exact params: going back to a range or store already
// seen does not repeat the (heavy) query. Only successful responses are kept,
// in memory until the page reloads.
const CACHE_TTL_MS = 5 * 60 * 1000;
const reportCache = new Map<
  string,
  { expiresAt: number; data: SubscriberCohortsReport }
>();

export function clearSubscriberCohortsCache() {
  reportCache.clear();
}

export function getSubscriberCohorts(
  params: SubscriberCohortsParams,
  cancelToken?: CancelToken
): Promise<SubscriberCohortsReport> {
  const key = JSON.stringify(toQueryParams(params));
  const cached = reportCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.data);
  }

  const endpoint = `${API_URL}/api/bi/subscriber-cohorts/`;
  return axios
    .get<SubscriberCohortsReport>(endpoint, {
      params: toQueryParams(params),
      cancelToken,
      timeout: REPORT_REQUEST_TIMEOUT_MS,
    })
    .then((response) => {
      const now = Date.now();
      reportCache.forEach((entry, entryKey) => {
        if (entry.expiresAt <= now) reportCache.delete(entryKey);
      });
      reportCache.set(key, {
        expiresAt: now + CACHE_TTL_MS,
        data: response.data,
      });
      return response.data;
    });
}

export function downloadSubscriberCohortsExcel(
  params: SubscriberCohortsParams & { retentionMode: RetentionMode }
): Promise<{ data: Blob }> {
  const endpoint = `${API_URL}/api/bi/subscriber-cohorts/download-excel/`;
  return axios.get(endpoint, {
    params: {
      ...toQueryParams(params),
      retention_mode: params.retentionMode,
    },
    responseType: 'blob',
    timeout: DOWNLOAD_REQUEST_TIMEOUT_MS,
  });
}

export function subscriberCohortsFilename({
  since,
  until,
}: SubscriberCohortsParams): string {
  return `cohorts-suscriptores-${since}_${until}.xlsx`;
}
